import React, { useCallback, useEffect, useRef } from 'react'
import './opportunity-section-navigation.scss'
import { useOpportunitySectionNavigation } from './opportunity-section-navigation-context'

const scrollPillIntoTrack = (
  track: HTMLDivElement,
  sectionId: string,
  behavior: ScrollBehavior = 'smooth',
) => {
  const pill = track.querySelector<HTMLButtonElement>(
    `[data-testid="opportunity-section-navigation-pill-${sectionId}"]`,
  )
  if (!pill) {
    return
  }

  const trackRect = track.getBoundingClientRect()
  const pillRect = pill.getBoundingClientRect()
  const isFullyVisible = pillRect.left >= trackRect.left && pillRect.right <= trackRect.right

  if (isFullyVisible) {
    return
  }

  const targetScrollLeft = pill.offsetLeft - track.clientWidth / 2 + pill.offsetWidth / 2

  track.scrollTo({
    left: Math.max(0, targetScrollLeft),
    behavior,
  })
}

const OpportunitySectionNavigationBar = () => {
  const { sections, activeSectionId, isVisible, navigateToSection } =
    useOpportunitySectionNavigation()
  const trackRef = useRef<HTMLDivElement>(null)

  const handlePillClick = useCallback(
    (sectionId: string) => {
      const track = trackRef.current
      if (track) {
        scrollPillIntoTrack(track, sectionId)
      }
      navigateToSection(sectionId)
    },
    [navigateToSection],
  )

  useEffect(() => {
    const track = trackRef.current
    if (!track || !isVisible || !activeSectionId) {
      return undefined
    }

    const frameId = window.requestAnimationFrame(() => {
      scrollPillIntoTrack(track, activeSectionId)
    })

    return () => window.cancelAnimationFrame(frameId)
  }, [activeSectionId, isVisible])

  if (!sections.length) {
    return null
  }

  return (
    <nav
      className={`opportunity-section-navigation-bar${
        isVisible ? ' opportunity-section-navigation-bar--visible' : ''
      }`}
      aria-label='Opportunity sections'
      data-testid='opportunity-section-navigation-bar'
      data-mobile-layout='bottom-dock'
    >
      <div className='opportunity-section-navigation-bar__gutter'>
        <div className='opportunity-section-navigation-bar__main'>
          <div
            ref={trackRef}
            className='opportunity-section-navigation-bar__track'
            role='tablist'
            aria-label='Jump to section'
          >
            {sections.map((section) => {
              const isActive = section.id === activeSectionId
              const label = section.label?.trim() || 'Section'
              return (
                <button
                  key={section.id}
                  type='button'
                  role='tab'
                  aria-selected={isActive}
                  className={`opportunity-section-navigation-bar__pill${
                    isActive ? ' opportunity-section-navigation-bar__pill--active' : ''
                  }`}
                  data-testid={`opportunity-section-navigation-pill-${section.id}`}
                  onClick={() => handlePillClick(section.id)}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </nav>
  )
}

export { OpportunitySectionNavigationBar }
